import type { CSSProperties } from "react";
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useAdminAuth, useSuperadminAuth } from "@/contexts/AdminAuthContext";
import { useAlbumNavigation } from "@/hooks/useAlbumNavigation";
import { LogOut, Settings, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@/contexts/ThemeProvider';
import { getSurfaceContrastColors, toSurfaceContrastVariables } from '@/lib/contrastUtils';

interface UserActionsProps {
  className?: string;
  variant?: 'default' | 'home';
}

type UserMetadata = {
  name?: string;
  full_name?: string;
  picture?: string;
  avatar_url?: string;
};

const UserActions = ({ className = "", variant = 'default' }: UserActionsProps) => {
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdminAuth();
  const { isSuperadmin } = useSuperadminAuth();
  const { getAlbumPath } = useAlbumNavigation();
  const navigate = useNavigate();
  const { tokens } = useTheme();

  const { t } = useTranslation();

  const buttonClasses = variant === 'home'
    ? "text-white/80 hover:text-white hover:bg-white/10"
    : "text-foreground/80 hover:text-foreground hover:bg-foreground/10";

  const getUserName = () => {
    if (!user) return 'Usuario';
    const userMeta = user.user_metadata as UserMetadata | undefined;
    return userMeta?.name || userMeta?.full_name || user.email?.split('@')[0] || 'Usuario';
  };

  const getUserAvatar = () => {
    if (!user) return undefined;
    const userMeta = user.user_metadata as UserMetadata | undefined;
    return userMeta?.picture || userMeta?.avatar_url;
  };

  const getUserInitials = () => {
    const name = getUserName();
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const primarySurface = tokens.colors.primary || tokens.colors.background || '0 0% 100%';
  const preferredPrimaryText = tokens.colors['primary-foreground'] || tokens.colors.foreground;
  const roleBadgeColors = useMemo(
    () => getSurfaceContrastColors(primarySurface, preferredPrimaryText),
    [preferredPrimaryText, primarySurface],
  );
  const roleBadgeStyle = useMemo<CSSProperties>(
    () => toSurfaceContrastVariables(roleBadgeColors) as CSSProperties,
    [roleBadgeColors],
  );

  if (!user) return null;

  return (
    <>
      <div className={`flex items-center gap-0 ${className}`}>
        <div className="relative ml-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={`${buttonClasses} !p-1 rounded-full`}
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={getUserAvatar()} />
                  <AvatarFallback className="text-xs bg-primary/10 text-primary">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-48 bg-background border shadow-lg z-50 flex flex-col space-y-3"
            >
              <DropdownMenuItem asChild>
                <Link to={getAlbumPath("/usuario")} className="flex items-center gap-2 cursor-pointer">
                  <User className="w-4 h-4" />
                  <span>{t('yourProfile')}</span>
                </Link>
              </DropdownMenuItem>

              {(isAdmin || isSuperadmin) && (
                <DropdownMenuItem
                  onClick={() => navigate(getAlbumPath("/admin"), { replace: true })}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Settings className="w-4 h-4" />
                  <span>{t('adminPanelTitle')}</span>
                </DropdownMenuItem>
              )}
              {/* {isSuperadmin && (
                <DropdownMenuItem
                  onClick={() => navigate(getAlbumPath("/admin/config"), { replace: true })}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <KeyRound className="w-4 h-4" />
                  <span>Configuración</span>
                </DropdownMenuItem>
              )} */}

              <DropdownMenuItem
                onClick={() => signOut()}
                className="flex items-center gap-2 cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
                <span>{t('signOut')}</span>
              </DropdownMenuItem>


            </DropdownMenuContent>
          </DropdownMenu>
          {(isSuperadmin || isAdmin) && (
            <div
              className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 px-1 py-0.5 bg-primary rounded pointer-events-none text-[9px] font-bold tracking-wider leading-none text-[hsl(var(--surface-fg))]"
              style={roleBadgeStyle}
            >
              {isSuperadmin ? "SUPER" : "ADMIN"}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UserActions;
